/**
 * Réžia udalostí: čo sa má hráčovi ukázať a v akom tempe.
 *
 * Engine vyhodnotí súboj naraz — odhalenie, hodnoty, poškodenie aj koniec
 * prídu v jednej dávke udalostí. Keby UI len preskočilo na nový stav, hráč by
 * videl, že niekomu ubudlo zdravie, ale nie prečo. Preto tu dávku rozložíme do
 * času: najprv súboj ohlásime, potom obe karty **otočíme naraz**, ukážeme
 * hodnoty a až potom poškodenie.
 *
 * Stav hry tým nemeníme — je to len divadlo nad udalosťami, ktoré už prišli.
 */

import type { CardId, FighterId, GameEvent } from '@unmatched/engine';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { BoardEffect } from '../board/renderer.js';

export interface FighterSnapshot {
  id: FighterId;
  owner: number;
  defId: string;
  name: string;
  maxHp: number;
  hp: number;
}

export type CombatPhase = 'declared' | 'flip' | 'values' | 'result';

export interface CombatShow {
  /** `seq` udalosti `combatStarted` — identita súboja. */
  key: number;
  attacker: FighterSnapshot;
  defender: FighterSnapshot;
  attackCard: CardId | null;
  defenseCard: CardId | null;
  /** Obranca sa nebránil. */
  passed: boolean;
  attackValue: number | null;
  defenseValue: number | null;
  /** Combat damage (bez poškodenia z efektov). */
  damage: number | null;
  hpAfter: number | null;
  defeated: boolean;
  cancelled: CardId[];
  phase: CombatPhase;
  /** Bonusový útok (Relentless Assault) — meno namiesto mena karty. */
  bonus?: string;
}

export interface TurnShow {
  key: number;
  player: number;
  turnNumber: number;
}

/** Tempo scény v ms. Súčet drží súboj pod piatimi sekundami. */
const LEAD = 700;
const FLIP = 850;
const VALUES = 700;
const HOLD = 2600;
const BANNER = 1500;

export function useStage(events: GameEvent[], fighters: FighterSnapshot[]) {
  const [combat, setCombat] = useState<CombatShow | null>(null);
  const [banner, setBanner] = useState<TurnShow | null>(null);
  const [effects, setEffects] = useState<BoardEffect[]>([]);

  const seen = useRef(0);
  const primed = useRef(false);
  const building = useRef<CombatShow | null>(null);
  const showing = useRef(false);
  const queue = useRef<TurnShow[]>([]);
  const timers = useRef<number[]>([]);
  /** Plávajúce čísla, ktoré čakajú na výsledok súboja na scéne. */
  const pendingEffects = useRef<BoardEffect[]>([]);
  /**
   * Posledná známa podoba každej figúrky. Porazená figúrka zo stavu zmizne,
   * ale scéna súboja ju ešte potrebuje ukázať — aj s tým, že padla.
   */
  const known = useRef(new Map<FighterId, FighterSnapshot>());
  for (const fighter of fighters) known.current.set(fighter.id, fighter);

  /**
   * Ktorý súboj scéna práve ukazuje (`key`). Časovače staršieho súboja ho
   * nesmú prepísať ani zavrieť — pri rýchlych ťahoch nový súboj začne skôr,
   * než dobehne výsledok toho predošlého.
   */
  const activeKey = useRef<number | null>(null);

  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const showNextBanner = useCallback(() => {
    if (showing.current) return;
    const next = queue.current.shift();
    if (!next) return;
    setBanner(next);
    later(BANNER, () => {
      setBanner(null);
      showNextBanner();
    });
  }, [later]);

  const finishCombat = useCallback(() => {
    showing.current = false;
    setCombat(null);
    showNextBanner();
  }, [showNextBanner]);

  /** Klik na scénu ju zavrie hneď — kto už videl, nemusí čakať. */
  const dismiss = useCallback(() => {
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
    // Plávajúce čísla zo zavretého súboja nesmú prepadnúť.
    const pendingFx = pendingEffects.current;
    pendingEffects.current = [];
    if (pendingFx.length) setEffects((current) => [...current, ...pendingFx].slice(-40));
    setBanner(null);
    finishCombat();
  }, [finishCombat]);

  useEffect(() => {
    const fresh = events.filter((event) => event.seq > seen.current);
    if (fresh.length === 0) return;
    seen.current = fresh[fresh.length - 1]!.seq;

    // Pri pripojení do rozohraného zápasu sa história nepreháva.
    if (!primed.current) {
      primed.current = true;
      return;
    }

    const snapshot = (id: FighterId): FighterSnapshot =>
      known.current.get(id) ?? { id, owner: 0, defId: '', name: id, maxHp: 0, hp: 0 };

    let started = false;
    let revealed = false;
    /** Prišli hodnoty? Súboj môže po otočení čakať na rozhodnutie hráča. */
    let valued = false;
    const immediateFx: BoardEffect[] = [];
    const deferredFx: BoardEffect[] = [];
    const turns: TurnShow[] = [];

    for (const event of fresh) {
      const current = building.current;
      switch (event.type) {
        case 'combatStarted':
          // Nový súboj v tej istej dávke: predošlý je dohraný, jeho čísla
          // idú na dosku hneď a príznaky platia odteraz len pre tento súboj.
          immediateFx.push(...deferredFx.splice(0));
          revealed = false;
          valued = false;
          building.current = {
            key: event.seq,
            attacker: snapshot(event.attackerFighter),
            defender: snapshot(event.defenderFighter),
            attackCard: null,
            defenseCard: null,
            passed: false,
            attackValue: null,
            defenseValue: null,
            damage: null,
            hpAfter: null,
            defeated: false,
            cancelled: [],
            phase: 'declared',
            ...(event.bonus ? { bonus: event.bonus } : {}),
          };
          started = true;
          break;
        case 'defenderChanged':
          // Patroklos prevzal súboj (Under Achilles' Helm) — scéna ukáže jeho.
          if (current) current.defender = snapshot(event.fighterId);
          break;
        case 'attackerChanged':
          // Smoke and Mirrors — do súboja ide druhá figúrka.
          if (current) current.attacker = snapshot(event.fighterId);
          break;
        case 'attackCardReplaced':
          // Sleight of Hand — otočí sa už nová karta.
          if (current) current.attackCard = event.to;
          break;
        case 'combatCardsRevealed':
          if (!current) break;
          current.attackCard = event.attackCard;
          current.defenseCard = event.defenseCard;
          current.passed = event.defenseCard === null;
          revealed = true;
          break;
        case 'effectCancelled':
          current?.cancelled.push(event.cardId);
          break;
        case 'combatValues':
          if (!current) break;
          valued = true;
          current.attackValue = event.attack;
          current.defenseValue = event.defense;
          current.damage = event.damage ?? Math.max(0, event.attack - event.defense);
          current.hpAfter = current.defender.hp;
          break;
        case 'damageDealt': {
          if (current && event.fighterId === current.defender.id && current.attackValue !== null) {
            // Figúrky sa ukladajú z už nového stavu, takže zdravie „pred" treba
            // dopočítať z prvej rany — inak by scéna ukázala 14 → 14 namiesto 16 → 14.
            if (current.hpAfter === current.defender.hp && !current.defeated) {
              current.defender = { ...current.defender, hp: event.hpAfter + event.amount };
            }
            current.hpAfter = event.hpAfter;
          }
          const fx: BoardEffect = {
            key: `d${event.seq}`,
            fighterId: event.fighterId,
            // 0 = poškodenie zastavila ochrana (Might of the Aen Elle).
            text: event.amount > 0 ? `−${event.amount}` : '0',
            tone: 'damage',
          };
          // Poškodenie zo súboja sa ukáže až s výsledkom na scéne, nie pred otočením kariet.
          (revealed || current?.attackCard ? deferredFx : immediateFx).push(fx);
          break;
        }
        case 'healed': {
          const fx: BoardEffect = {
            key: `h${event.seq}`,
            fighterId: event.fighterId,
            text: `+${event.amount}`,
            tone: 'heal',
          };
          (revealed || current?.attackCard ? deferredFx : immediateFx).push(fx);
          break;
        }
        case 'fighterDefeated':
          if (current && event.fighterId === current.defender.id) current.defeated = true;
          break;
        case 'turnStarted':
          turns.push({ key: event.seq, player: event.player, turnNumber: event.turnNumber });
          break;
        default:
          break;
      }
    }

    if (immediateFx.length) setEffects((current) => [...current, ...immediateFx].slice(-40));

    const record = building.current;
    /** Hodnoty a výsledok od `from` ms; scéna potom zmizne. */
    const playOut = (from: number) => {
      building.current = null;
      pendingEffects.current = [...pendingEffects.current, ...deferredFx];
      const mine = () => activeKey.current === record!.key;
      later(from, () => mine() && setCombat({ ...record!, phase: 'values' }));
      later(from + VALUES, () => {
        if (!mine()) return;
        setCombat({ ...record!, phase: 'result' });
        const fx = pendingEffects.current;
        pendingEffects.current = [];
        if (fx.length) setEffects((current) => [...current, ...fx].slice(-40));
      });
      later(from + VALUES + HOLD, () => mine() && finishCombat());
    };
    if (record && (started || revealed)) {
      showing.current = true;
      activeKey.current = record.key;
      if (!revealed) {
        // Útok je ohlásený, obranca ešte vyberá — scéna počká s kartami lícom dole.
        setCombat({ ...record, phase: 'declared' });
      } else {
        const lead = started ? LEAD : 0;
        if (started) setCombat({ ...record, phase: 'declared' });
        later(lead, () => activeKey.current === record.key && setCombat({ ...record, phase: 'flip' }));
        if (valued) {
          playOut(lead + FLIP);
        } else {
          // Karty sú otočené, ale súboj čaká na rozhodnutie (The Day of Your
          // Doom, Under Achilles' Helm) — hodnoty prídu v ďalšej dávke.
          pendingEffects.current = deferredFx;
        }
      }
    } else if (record && valued) {
      // Pokračovanie prerušeného súboja: karty už sú otočené.
      playOut(0);
    }

    if (turns.length) {
      queue.current.push(...turns);
      showNextBanner();
    }
  }, [events, later, finishCombat, showNextBanner]);

  useEffect(
    () => () => {
      for (const id of timers.current) window.clearTimeout(id);
    },
    [],
  );

  return { combat, banner, effects, dismiss };
}
